/**
 * Helpers for turning a single brand hex colour into the HSL values that the
 * design tokens in `src/index.css` expect (space separated "H S% L%").
 */

export const DEFAULT_BRAND_COLOR = '#578FC8';

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const isValidHexColor = (value: unknown): value is string =>
  typeof value === 'string' && HEX_RE.test(value.trim());

/** Expands `#abc` to `#aabbcc` and lowercases. Returns '' when invalid. */
export const normaliseHexColor = (value: unknown): string => {
  if (!isValidHexColor(value)) return '';
  const hex = (value as string).trim().toLowerCase();
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
};

interface Hsl {
  h: number;
  s: number;
  l: number;
}

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const full = normaliseHexColor(hex) || DEFAULT_BRAND_COLOR;
  return {
    r: parseInt(full.slice(1, 3), 16),
    g: parseInt(full.slice(3, 5), 16),
    b: parseInt(full.slice(5, 7), 16),
  };
};

export const hexToHsl = (hex: string): Hsl => {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const clamp = (value: number, min = 0, max = 100) => Math.min(max, Math.max(min, value));

/** Formats an HSL triple the way the CSS variables in index.css are written. */
export const hslToTokenString = ({ h, s, l }: Hsl): string =>
  `${h} ${clamp(s)}% ${clamp(l)}%`;

/** Same hue, shifted lightness — used for the light/dark accent aliases. */
export const shiftLightness = (hsl: Hsl, delta: number): Hsl => ({
  ...hsl,
  l: clamp(hsl.l + delta),
});

/** Relative luminance (WCAG) of a hex colour, 0 (black) to 1 (white). */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * The CSS variable overrides for a brand colour. Applied inline on
 * `documentElement` so both the light and dark token blocks inherit them.
 */
export const brandColorCssVariables = (hex: string): Record<string, string> => {
  const base = hexToHsl(hex);
  const token = hslToTokenString(base);
  // White label text on mid/dark colours, near-black on pale ones.
  const foreground = relativeLuminance(hex) > 0.6 ? '222 47% 11%' : '0 0% 100%';

  return {
    '--primary': token,
    '--primary-foreground': foreground,
    '--ring': token,
    '--sidebar-primary': token,
    '--sidebar-primary-foreground': foreground,
    '--sidebar-ring': token,
    // Legacy accent aliases that currently point at the same brand colour.
    '--gold': token,
    '--gold-light': hslToTokenString(shiftLightness({ ...base, s: clamp(base.s + 4) }, 37)),
    '--gold-dark': hslToTokenString(shiftLightness({ ...base, s: clamp(base.s + 7) }, -20)),
    '--teal': token,
    '--ocean-500': token,
    '--ocean-300': hslToTokenString(shiftLightness({ ...base, s: clamp(base.s + 4) }, 16)),
    '--shadow-gold': `0 4px 20px hsl(${token} / 0.25)`,
  };
};

export const BRAND_COLOR_PRESETS: { label: string; value: string }[] = [
  { label: 'Blue', value: DEFAULT_BRAND_COLOR },
  { label: 'Teal', value: '#0f9488' },
  { label: 'Green', value: '#2f8f4e' },
  { label: 'Indigo', value: '#4f56d3' },
  { label: 'Purple', value: '#7c46c9' },
  { label: 'Red', value: '#c8393c' },
  { label: 'Amber', value: '#d08b1c' },
  { label: 'Slate', value: '#47566b' },
];
